import React, { useEffect, useState } from "react";
import { GlobalState } from "store/configure-store";
import { connect } from "react-redux";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import {
  selectIsEAttest,
  selectPatient,
  selectPayedAmount,
  selectPaymentMethod,
  selectTarificationFinishedResult,
  selectTarificationSessionForPatient,
  selectTarifiedToday,
} from "store/reducers/patient.reducer";
import * as _ from "lodash";
import { selectUser } from "store/reducers/user-context.reducer";
import { selectTarificationApplicationSession } from "store/reducers/tarification.reducer";
import {
  finishTarificationSession,
  FinishTarificationSessionType,
} from "store/actions/tarification.actions";
import { usePrevious } from "common/hooks/previous.hook";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { closePatient } from "store/actions/patient.actions";
import { PaymentMethodEnum } from "common/enums/PaymentMethodEnum";
import { User } from "model/user.model";
import {
  Alert,
  Button,
  Card,
  Container,
  Icon,
  Spinner,
} from "@cc/react-components";
import { TarificationApplicationSession } from "model/tarification.model";
import { HelenaSessionKey } from "model/helena.model";
import { Patient } from "model/patient.model";
import {
  selectCanUseHelena,
  selectHelenaPatientId,
  selectSessionKey,
} from "store/reducers/helena.reducer";
import { AttestationProgress } from "model/attestion.model";
import { Col, Row } from "@cc/react-components/Grid";
import { createAttestationHE } from "store/actions/helena.actions";
import Page from "ui/Page";
import { PV_CONSULTATION_FINISHED } from "common/matomo/track-page-view.constants";
import { useTrackPageView } from "common/matomo/track-page-view.hook";

export interface Props
  extends React.HTMLAttributes<HTMLElement>,
    RouteComponentProps {
  inView: boolean;
}

const ConsultationFinishedContainer = ({
  payedAmount,
  paymentMethod,
  eAttest,
  user,
  tApplicationSession,
  tSession,
  attestationProgress,
  finishTarificationSession,
  inView,
  history,
  closePatient,
  patient,
  helenaPatientId,
  helenaSessionKey,
  createAttestationHE,
  tarifiedToday,
  canUseHelena,
}: StateFromProps & DispatchFromProps & Props) => {
  const [t] = useTranslation();
  const prevInView = usePrevious(inView);
  const [helenaPdf, setHelenaPdf] = useState<any>();
  const [ignoreWarning, setIgnoreWarning] = useState(false);
  const [busy, setBusy] = useState(false);

  useTrackPageView(PV_CONSULTATION_FINISHED);

  useEffect(() => {
    if (
      inView &&
      !busy &&
      tApplicationSession != null &&
      tSession != null &&
      user.id > 0 &&
      helenaSessionKey !== null &&
      helenaPatientId !== null &&
      !helenaPdf &&
      !(tarifiedToday && eAttest)
    ) {
      setBusy(true);
      finishTarificationSession(
        tApplicationSession,
        tSession,
        user.id,
        payedAmount,
        paymentMethod,
        eAttest,
        history,
        patient.id,
        helenaSessionKey,
        helenaPatientId,
        canUseHelena
      )
        .then((pdf) => setHelenaPdf(pdf))
        .finally(() => {
          setIgnoreWarning(false);
          setBusy(false);
        });
    }
  }, [
    inView,
    prevInView,
    tSession,
    tApplicationSession,
    user.id,
    payedAmount,
    paymentMethod,
    eAttest,
    finishTarificationSession,
    history,
    patient,
    helenaSessionKey,
    helenaPatientId,
    helenaPdf,
    tarifiedToday,
    ignoreWarning,
    busy,
    canUseHelena,
  ]);

  useEffect(() => {
    if (inView && attestationProgress === AttestationProgress.DONE) {
      _.delay(() => {
        closePatient();
        history.replace("/");
      }, 1000);
    }
  }, [inView, attestationProgress, closePatient, history]);

  const renderLoading = (
    toStart: boolean,
    loading: boolean,
    errorMessage: boolean,
    key: string
  ) => {
    return (
      <Row className="align-items-center pb-2 no-gutters">
        <Col xs={2}>
          {errorMessage && (
            <Icon type="report" className="text-danger" size="lg" />
          )}
          {toStart && !errorMessage && (
            <Icon type="info" className="text-muted" size="lg" />
          )}
          {!loading && !errorMessage && !toStart && (
            <Icon type="success-circle" className="text-success" size="lg" />
          )}
          {loading && !errorMessage && !toStart && (
            <Spinner className="text-success ml-1" size="sm" />
          )}
        </Col>
        <Col xs={10}>
          <p className="my-1">{t(key)}</p>
        </Col>
      </Row>
    );
  };

  const retryHelena = () => {
    const helenaPdfBackup = helenaPdf;
    setHelenaPdf(undefined);
    createAttestationHE(
      patient.id,
      user.id,
      helenaSessionKey,
      helenaPatientId,
      helenaPdf
    )
      .then()
      .catch((e) => {
        setHelenaPdf(helenaPdfBackup);
      });
  };

  return (
    <Page.Scrollable>
      <Page.Body>
        <Page.Title className="font-weight-bold">
          {t("consultation.finished.title")}
        </Page.Title>
        {tarifiedToday && eAttest && (
          <section>
            <Alert variant="danger">
              {t("consultation.finished.notPossible")}
            </Alert>
          </section>
        )}
        <section>
          <Card>
            <Card.Body>
              <Container>
                {renderLoading(
                  attestationProgress === AttestationProgress.NEW,
                  attestationProgress > AttestationProgress.ERROR_HELENA &&
                    attestationProgress < AttestationProgress.START_HELENA,
                  attestationProgress ===
                    AttestationProgress.ERROR_TARIFICATION,
                  "consultation.finished.tarification"
                )}
                {canUseHelena &&
                  renderLoading(
                    attestationProgress === AttestationProgress.NEW,
                    attestationProgress > AttestationProgress.ERROR_HELENA &&
                      attestationProgress < AttestationProgress.DONE,
                    attestationProgress === AttestationProgress.ERROR_HELENA,
                    "consultation.finished.helena"
                  )}
              </Container>
            </Card.Body>
          </Card>
        </section>
        {helenaPdf && (
          <section>
            <Button onClick={retryHelena} variant="danger" block>
              <p className="m-0">{t("consultation.finished.helenaRetry")}</p>
            </Button>
          </section>
        )}
      </Page.Body>
    </Page.Scrollable>
  );
};

interface StateFromProps {
  payedAmount: number;
  paymentMethod: PaymentMethodEnum;
  eAttest: boolean;
  user: User;
  tApplicationSession: TarificationApplicationSession;
  tSession?: TarificationSessionKey;
  attestationProgress: AttestationProgress;
  patient: Patient;
  helenaSessionKey: HelenaSessionKey | null;
  helenaPatientId: string | null;
  tarifiedToday: boolean;
  canUseHelena: boolean;
}

const mapStateToProps = (state: GlobalState): StateFromProps => {
  return {
    payedAmount: selectPayedAmount(state),
    paymentMethod: selectPaymentMethod(state),
    eAttest: selectIsEAttest(state),
    user: selectUser(state),
    tApplicationSession: selectTarificationApplicationSession(state),
    tSession: selectTarificationSessionForPatient(state),
    attestationProgress: selectTarificationFinishedResult(state),
    patient: selectPatient(state),
    helenaPatientId: selectHelenaPatientId(state.helena),
    helenaSessionKey: selectSessionKey(state.helena),
    tarifiedToday: selectTarifiedToday(state),
    canUseHelena: selectCanUseHelena(state.helena),
  };
};

interface DispatchFromProps {
  finishTarificationSession: FinishTarificationSessionType;
  createAttestationHE: (
    patientId: number,
    userId: number,
    helenaSessionKey: HelenaSessionKey | null,
    helenaPatientId: string | null,
    pdf: string
  ) => Promise<void>;
  closePatient: () => void;
}

export default withRouter(
  connect(mapStateToProps, {
    finishTarificationSession,
    closePatient,
    createAttestationHE,
  })(ConsultationFinishedContainer)
);
